import React, { useContext, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import Header from "./Header";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState(
    (product && product.size && product.size[0]) || ""
  );
  const [selectedColor, setSelectedColor] = useState(
    (product && product.colors && product.colors[0]) || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, 1, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const setImage = (idx) => {
    setSelectedImage(idx);
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price).toLocaleString()}`;
  };

  return (
    <div className="product-detail">
      <Header />

      <div className="product-detail__container">
        {/* Left - Breadcrumb and Thumbnails */}
        <div className="left-column">
          <nav className="breadcrumb">
            <Link to="/" onClick={toTop}>
              Home
            </Link>
            <span className="separator">▸</span>
            <Link to="/shop" onClick={toTop}>
              {product.category}
            </Link>
            <span className="separator">▸</span>
            <span className="current">{product.name}</span>
          </nav>

          {/* Thumbnail Strip */}
          {product.images && product.images.length > 1 && (
            <div className="thumbnail-strip vertical">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  className={`thumb ${idx === selectedImage ? "active" : ""}`}
                  onClick={() => setImage(idx)}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={img} alt={`${product.name} view ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Center - Main Image */}
        <div className="center-column">
          <div className="main-image">
            <img
              src={
                (product.images && product.images[selectedImage]) || product.image
              }
              alt={product.name}
            />
          </div>
        </div>

        {/* Right - Product Info */}
        <aside className="right-column">
          <div className="product-meta">
            <h1 className="product-name">{product.name}</h1>
            <div className="product-price">{formatPrice(product.price)}</div>
          </div>

          <div className="product-description">
            {product.description.map((des, index) => (
              <p key={index}>{des}</p>
            ))}

            <ul>
              {product.Features.map((des, index) => (
                <li key={index}>{des}</li>
              ))}
            </ul>
          </div>

          {/* Hidden Original Content to preserve data integrity if needed */}
          <div style={{ display: "none" }}>
            <div className="product-reviews">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`star ${
                      i < Math.floor(product.rating) ? "filled" : ""
                    }`}
                  >
                    ★
                  </span>
                ))}
              </div>
              <span className="review-count">
                {`${product.rating} (${product.reviews} reviews)`}
              </span>
            </div>

            <div className="product-options">
              <div className="option-group">
                <label className="option-label">Color</label>
                <div className="selected-value">{selectedColor}</div>
                <div className="color-swatches">
                  {product.colors &&
                    product.colors.map((color, idx) => (
                      <button
                        key={idx}
                        className={`swatch ${
                          selectedColor === color ? "selected" : ""
                        }`}
                        onClick={() => setSelectedColor(color)}
                        title={color}
                        style={{
                          backgroundColor: getColorHex(color),
                        }}
                      />
                    ))}
                </div>
              </div>

              <div className="option-group">
                <label className="option-label">Size</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="size-select"
                >
                  {product.size &&
                    product.size.map((s, idx) => (
                      <option key={idx} value={s}>
                        {s}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="accordion-section">
              <details className="accordion">
                <summary>DETAILS AND CARE</summary>
                <div className="accordion-content">
                  <ul className="features-list">
                    {product.Features &&
                      product.Features.map((f, index) => (
                        <li key={index}>{f}</li>
                      ))}
                  </ul>
                </div>
              </details>

              <details className="accordion">
                <summary>DELIVERY AND RETURNS</summary>
                <div className="accordion-content">
                  <p>Complimentary shipping on orders over $2,500.</p>
                  <p>Express delivery within 3-5 business days.</p>
                  <p>Complimentary gift wrapping included.</p>
                  <p>Free returns within 14 days.</p>
                </div>
              </details>
            </div>
          </div>

          <div className="product-actions-bottom">
            <div className="stock-status">In Stock</div>
            <button className="add-to-cart-btn" onClick={handleAddToCart}>
              {`${formatPrice(product.price)} - Add To Cart`}
            </button>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
};

// Helper function to convert color names to hex
function getColorHex(colorName) {
  const colors = {
    Gold: "#D4AF37",
    Onyx: "#1a1a1a",
    Lapis: "#26619C",
    Pearl: "#F5F5DC",
    Silver: "#C0C0C0",
    Carnelian: "#B8653A",
    Turquoise: "#40E0D0",
    Jade: "#00A86B",
    Quartz: "#E6E6FA",
    Cognac: "#9A463D",
    Black: "#000000",
    Ivory: "#FFFFF0",
    Gunmetal: "#2A3439",
    Blush: "#FFB6C1",
    Stone: "#928E85",
    Tan: "#D2B48C",
    Olive: "#808000",
    Charcoal: "#36454F",
    Rose: "#FF007F",
    Heather: "#9B8FA0",
    Mocha: "#967969",
    Mulberry: "#8E4585",
    Brown: "#8B4513",
    Beige: "#F5F5DC",
    "Plum Blur": "#8E4585",
    "Trail Green": "#4A5D23",
    "Deep Red": "#8B0000",
    "Midnight Blue": "#191970",
    White: "#FFFFFF",
    Sand: "#C2B280",
    Taupe: "#483C32",
    Neutral: "#C3B091",
    "Cognac Swirl": "#9A463D",
    Indigo: "#4B0082",
    "Light Blue": "#ADD8E6",
    Leopard: "#C19A6B",
    "18k Gold": "#D4AF37",
    "Sterling Silver": "#C0C0C0",
    "Gold Vermeil": "#D4AF37",
  };
  return colors[colorName] || "#888888";
}

export default ProductDetail;
